import { Icon, Section, useToast } from '@vallourec-ui/vallourec-ui'
import React, { useEffect, useRef, useState } from 'react'

import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import {
  ButtonContainer,
  Row,
  SearchBarContainer,
  StyledButton,
  StyledInputLabel,
  StyledTextButton,
} from './style'

import { DataResultTemplate } from '@/@types/global'
import { tpiSampleTracking as tpiSampleTrackingPost } from '@/api/post'
import TextFieldInput from '@/components/TextFieldInput'
import { generateObject } from '@/report/generateTest'
import { useApp } from '@/stores/AppStore'
import { useSearchReportOrders } from '@/stores/SearchReportOrdersStore'

const FilterSection: React.FC = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { setIsLoading, setTemplateColumn, setTemplateRow, setResultItems } =
    useApp()
  const {
    order,
    setOrder,
    item,
    setItem,
    processOrder,
    setProcessOrder,
    setTemplateTest,
  } = useSearchReportOrders()

  const validate = () => {
    let valid = true
    if (!order && !item && !processOrder) {
      toast({
        title: 'Error',
        description: t('searchReportOrders.filterSection.required_filter'),
        type: 'error',
      })
      valid = false
    }

    return valid
  }

  const executeQuery = async () => {
    if (validate()) {
      try {
        setIsLoading(true)
        const resultFirst = await tpiSampleTrackingPost({
          search: {
            order: order ?? '',
            item: item ?? '',
            productionOrder: processOrder ?? null,
          },
        })
        const result = await tpiSampleTrackingPost({
          search: {
            order: order ?? '',
            item: item ?? '',
            productionOrder: processOrder ?? null,
          },
          items: resultFirst.report,
        })

        const template = generateObject('searchReportOrders')
        setTemplateTest(template)

        const res: { columns: DataResultTemplate[]; row: any[] } = {
          columns: [],
          row: [],
        }
        if (template) {
          res.columns = template.map((elem) => {
            return {
              id: elem.id,
              accessorKey: elem.id,
              header: t(elem.header),
              width: elem.width,
              cell: ({ cell }) => {
                return <div>{cell.getValue()}</div>
              },
            } as DataResultTemplate
          })
          res.row = result.report.map((item: any, index: number) => {
            item.checked = true
            const objectRow = {
              ...Object.entries(item).reduce((acc, [key, value]) => {
                if (value) {
                  const valueDiv = value
                  acc[key] = (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {String(valueDiv)}
                    </div>
                  ) as React.ReactNode
                }
                return acc
              }, {}),
              index,
            }
            return objectRow
          })
        }
        setTemplateColumn(res.columns)
        setTemplateRow(res.row)
        setResultItems(result?.report)
      } catch (error) {
        console.log(error)
        if (isAxiosError(error)) {
          toast({
            title: 'Error',
            description: error.response?.data?.errorMessage ?? error.message,
            type: 'error',
          })
        } else {
          toast({
            title: 'Error',
            description: `${error}`,
            type: 'error',
          })
        }
      } finally {
        setIsLoading(false)
      }
    } else {
      setTemplateColumn([])
      setTemplateRow([])
    }
  }

  const clearResults = () => {
    setTemplateColumn([])
    setTemplateRow([])
    setResultItems([])
  }

  const search = async () => {
    try {
      setIsLoading(true)
      clearResults()
      await executeQuery()
    } catch (error) {
      console.log('error', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      clearResults()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Section.Root
      header={
        <Section.Header
          title={t<string>('searchReportOrders.filterSection.title')}
          isCollapsable
          actionButtons={
            <ButtonContainer width="8rem">
              <StyledButton onClick={() => search()}>
                <StyledTextButton>
                  <Icon name="SearchLine" color="white" />
                  {t<string>('searchReportOrders.filterSection.search')}
                </StyledTextButton>
              </StyledButton>
            </ButtonContainer>
          }
        />
      }
    >
      <Row>
        <SearchBarContainer>
          <StyledInputLabel>
            {t('searchReportOrders.filterSection.order')}
          </StyledInputLabel>
          <TextFieldInput
            fontSize="text-md"
            name="order"
            onChange={(event) => setOrder(event?.target?.value)}
            placeholder={t('', t('searchReportOrders.filterSection.order'))}
            width="100%"
            value={order}
          />
        </SearchBarContainer>
        <SearchBarContainer>
          <StyledInputLabel>
            {t('searchReportOrders.filterSection.item')}
          </StyledInputLabel>
          <TextFieldInput
            fontSize="text-md"
            name="item"
            onChange={(event) => setItem(event?.target?.value)}
            placeholder={t('', t('searchReportOrders.filterSection.item'))}
            width="100%"
            value={item}
          />
        </SearchBarContainer>
        <SearchBarContainer>
          <StyledInputLabel>
            {t('searchReportOrders.filterSection.processOrder')}
          </StyledInputLabel>
          <TextFieldInput
            fontSize="text-md"
            name="processOrder"
            onChange={(event) => setProcessOrder(event?.target?.value)}
            placeholder={t(
              '',
              t('searchReportOrders.filterSection.processOrder')
            )}
            width="100%"
            value={processOrder}
          />
        </SearchBarContainer>
      </Row>
    </Section.Root>
  )
}

export default FilterSection
